// Identity-bound lower third: the name strap over the program feed is
// generated from the named participant's atproto profile rather than uploaded
// art. Nothing but the identity rides in room metadata, so the name on screen
// is always the DID that actually published the track — it can't be typed
// wrong, and it can't drift once the cut changes.
//
// Shared by the member dock and the public watch page; each supplies profiles
// from its own cache, since the two bundles resolve them differently.

export interface LowerThirdProfile {
  handle?: string
  displayName?: string
  avatar?: string
}

export interface LowerThirdLayer {
  // `key` is the participant identity the strap names, or null for off air.
  // Safe to call on every metadata change: the slide-in only replays when the
  // named person actually changes, so a late-arriving profile can fill the
  // text in without restarting the animation.
  set(key: string | null, profile?: LowerThirdProfile): void
  destroy(): void
}

// `initial` is the identity already on air, for hosts that rebuild their DOM
// on every state change (the member dock does). Seeding it means a re-render
// mid-broadcast redraws the strap in place instead of sliding it back in.
export function mountLowerThird(stage: HTMLElement, initial?: string | null): LowerThirdLayer {
  const strap = document.createElement('div')
  strap.className = initial ? 'lower-third on' : 'lower-third'
  const avatar = document.createElement('img')
  avatar.className = 'lt-avatar'
  avatar.alt = ''
  const text = document.createElement('div')
  text.className = 'lt-text'
  const name = document.createElement('span')
  name.className = 'lt-name'
  const handle = document.createElement('span')
  handle.className = 'lt-handle'
  text.append(name, handle)
  strap.append(avatar, text)
  stage.append(strap)

  let current: string | null = initial ?? null

  return {
    set(key, profile) {
      if (!key) {
        current = null
        strap.classList.remove('on')
        return
      }
      const p = profile ?? {}
      // Before the profile resolves, the handle-less fallback still reads as a
      // person rather than flashing an empty bar.
      name.textContent = p.displayName || (p.handle ? `@${p.handle}` : '…')
      handle.textContent = p.displayName && p.handle ? `@${p.handle}` : ''
      if (p.avatar) {
        avatar.src = p.avatar
        avatar.style.display = ''
      } else {
        avatar.removeAttribute('src')
        avatar.style.display = 'none'
      }
      if (key !== current) {
        current = key
        strap.classList.add('on')
      }
    },
    destroy() {
      strap.remove()
    },
  }
}
